import DiagramGenerator from './common/DiagramGenerator';
import DocumentationGenerator from './common/DocumentationGenerator';
import type Diagram from './common/Diagram';
import type { RenderToWhiteboardOptions } from './common/Diagram';
import colorSwatchSvg from './assets/colorSwatch.svg';
import { colorSchemes } from './colors';
import DiagramError from './common/DiagramError';
import { type MeasureText } from './types';
import type { DiagramNode } from './parsing/DiagramStructure';

// External API for diagram generation.

// Return lists of diagram variants.

// Return allowed properties for a specific diagram variant.

// Generate a diagram from a string input.
function generateDiagram(
  input: string,
  { widthHint, heightHint }: { widthHint: number; heightHint: number }
): Diagram {
  return DiagramGenerator.generate(input, { widthHint, heightHint });
}

type DiagramVariantDefinition = {
  value: string;
  name: string;
  icon: string;
};

type DiagramVariantSection = {
  diagramName: string;
  diagramType: string;
  variants: DiagramVariantDefinition[];
};

// The variants of diagrams that can be generated. Used to generate a menu for
// choosing between possible diagrams.
function diagramVariants(): DiagramVariantSection[] {
  const variantSections: DiagramVariantSection[] = [];

  for (const diagramClass of DiagramGenerator.diagramClasses()) {
    const variantProperty = diagramClass.allowedProperties.variant;

    if (!variantProperty) continue;
    if (variantProperty.type === 'number') continue;

    variantSections.push({
      diagramName: diagramClass.diagramName,
      diagramType: diagramClass.diagramType,
      variants: variantProperty.options.map(o => ({
        value: o.value as string,
        name: o.name,
        icon: o.icon!,
      })),
    });
  }

  return variantSections;
}

type DiagramColorSchemeDefinition = {
  value: string;
  name: string;
  icon: string;
};

function diagramColorSchemes(): DiagramColorSchemeDefinition[] {
  const swatchIcon: string = colorSwatchSvg;

  return (
    colorSchemes.map(c => ({
      name: c.title,
      value: c.value,
      icon: swatchIcon
        .replaceAll('#color1', c.swatchColors[0])
        .replaceAll('#color2', c.swatchColors[1])
        .replaceAll('#color3', c.swatchColors[2]),
    })) || []
  );
}

// Set a diagram level property on the input string.
function diagramSetProperty(
  input: string,
  propertyName: string,
  propertyValue: string | number
): string {
  // Attempt to parse the input.
  const parseOutput = DiagramGenerator.parse(input);

  if (parseOutput.result === 'success') {
    const database = DiagramGenerator.translateSyntaxTree(parseOutput);

    database.setProperty(propertyName, propertyValue);

    // Convert the output back to a string.
    return DiagramGenerator.toString(parseOutput);
  }

  console.log('Unable to update unparsable input');

  return input;
}

function diagramSetVariant(
  input: string,
  diagramType: string,
  variant: string
): string {
  // Attempt to parse the input.
  const parseOutput = DiagramGenerator.parse(input);

  if (parseOutput.result === 'success') {
    const database = DiagramGenerator.translateSyntaxTree(parseOutput);
    if (database.diagramTypeParseNode) {
      database.setDiagramType(diagramType);
      database.setProperty('variant', variant);
      return DiagramGenerator.toString(parseOutput);
    } else {
      // Fall through to default case.
    }
  }

  // If we can't parse the input or find a diagram type node, then just
  // prepend the default diagram type and property.
  return `${diagramType} { variant: ${variant} }\n\n${input}`;
}

export {
  DocumentationGenerator,
  generateDiagram,
  diagramVariants,
  diagramColorSchemes,
  diagramSetProperty,
  diagramSetVariant,
  type Diagram,
  type DiagramNode,
  type MeasureText,
  type DiagramVariantDefinition,
  type DiagramVariantSection,
  type DiagramColorSchemeDefinition,
  type RenderToWhiteboardOptions,
  DiagramError,
};
